import React, { useEffect, useState } from "react";
import { Form, Button, Select, Dropdown, Modal, Header } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import {
  LegalRequirement,
  Area,
  Source,
  Authority,
  Responsible,
  Frequency,
  Site,
  Requirement,  
} from "../../../../api";
import { useAuth } from "../../../../hooks";
import { AreaForm } from "../../Areas/AreaForm";

const lrController         = new LegalRequirement();
const areaController       = new Area();
const sourceController     = new Source();
const authorityController  = new Authority();
const responsibleController= new Responsible();
const frequencyController  = new Frequency();
const siteController          = new Site();
const requirementController   = new Requirement();

const EVIDENCE_TYPE_OPTIONS = [
  { key: "licencia",            value: "licencia",            text: "Licencia" },
  { key: "permiso",             value: "permiso",             text: "Permiso" },
  { key: "informe",             value: "informe",             text: "Informe / Análisis / Monitoreo" },
  { key: "registros_firmados",  value: "registros_firmados",  text: "Registros firmados" },
  { key: "ddjj",                value: "ddjj",                text: "DDJJ — Declaración jurada" },
  { key: "contrato_servicio",   value: "contrato_servicio",   text: "Contrato de servicio vigente" },
  { key: "certificado",         value: "certificado",         text: "Certificado profesional" },
  { key: "constancia_dc3",      value: "constancia_dc3",      text: "Constancia DC-3 (MEX)" },
  { key: "acta_inspeccion",     value: "acta_inspeccion",     text: "Acta de inspección" },
  { key: "fotografia",          value: "fotografia",          text: "Fotografía / evidencia visual" },
  { key: "otro",                value: "otro",                text: "Otro" },
];

const STATUS_OPTIONS = [
  { key: "vigente",     value: "vigente",     text: "Vigente" },
  { key: "vencido",     value: "vencido",     text: "Vencido" },
  { key: "pendiente",   value: "pendiente",   text: "Pendiente" },
  { key: "en_revision", value: "en_revision", text: "En revisión" },
];

export function LegalRequirementForm(props) {
  const { item, close, onReload } = props;
  const { accessToken } = useAuth();

  const [areas,          setAreas]          = useState([]);
  const [sources,        setSources]        = useState([]);
  const [authorities,    setAuthorities]    = useState([]);
  const [responsibles,   setResponsibles]   = useState([]);
  const [frequencies,    setFrequencies]    = useState([]);
  const [requirements,   setRequirements]   = useState([]);
  const [sites,          setSites]          = useState([]);
  const [showAreaModal,  setShowAreaModal]  = useState(false);

  const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));

  const reloadAreas = async (selectNew = false) => {
    try {
      const prevIds = areas.map((a) => a.key);
      const fetched = await areaController.getAreas(accessToken);
      const opts = toOpts(fetched);
      setAreas(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) formik.setFieldValue("area", added.value);
      }
    } catch { /* ignorar */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [a, s, au, res, fr, si, req] = await Promise.allSettled([
          areaController.getAreas(accessToken),
          sourceController.getSources(accessToken),
          authorityController.getAuthorities(accessToken),
          responsibleController.getResponsibles(accessToken),
          frequencyController.getFrequencies(accessToken),
          siteController.getSites(accessToken, true),
          requirementController.getRequirements(accessToken), 
        ]);
        setAreas(        a.status  === "fulfilled" ? toOpts(a.value)   : []);
        setSources(      s.status  === "fulfilled" ? toOpts(s.value)   : []);
        setAuthorities(  au.status === "fulfilled" ? toOpts(au.value)  : []);
        setResponsibles( res.status=== "fulfilled" ? toOpts(res.value) : []);
        setFrequencies(  fr.status === "fulfilled" ? toOpts(fr.value)  : []);
        setSites(        si.status === "fulfilled" ? toOpts(si.value)  : []);
        setRequirements( req.status=== "fulfilled" ? toOpts(req.value): []);
      } catch { /* catalogos vacíos si falla */ }
    })();
  }, []);

  const getId = (val) => (val && typeof val === "object" ? val._id : val) || "";

  const formik = useFormik({
    initialValues: {
      area:        getId(item?.area),
      source:      getId(item?.source),
      authority:   getId(item?.authority),
      responsible: getId(item?.responsible),
      frequency:   getId(item?.frequency),
      site:        getId(item?.site),
      requirement: getId(item?.requirement),
      expiration:    item?.expiration ? item.expiration.split("T")[0] : "",
      status:        item?.status        || "",
      progress:      item?.progress      ?? 0,
      observations:  item?.observations  || "",
      evidenceTypes: item?.evidenceTypes || [],
      active:        item?.active        ?? true,
    },
    validationSchema: Yup.object({
      area: Yup.string().required("El área es requerida"),
      site: Yup.string().required("El sitio es requerido"),
    }),
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Limpiar campos vacíos de tipo ObjectId
        const refFields = ["area","source","authority","responsible","frequency","site","requirement"];
        const payload = { ...values };
        refFields.forEach((f) => { if (!payload[f]) delete payload[f]; });

        if (item) {
          await lrController.updateLegalRequirement(accessToken, item._id, payload);
          toast.success("Requisito legal actualizado");
        } else {
          await lrController.createLegalRequirement(accessToken, payload);
          toast.success("Requisito legal creado");
        }
        onReload();
        close();
      } catch (err) {
        toast.error(err?.msg || "Error al guardar");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const sf = (field) => (_, d) => formik.setFieldValue(field, d.value);

  return (
    <Form onSubmit={formik.handleSubmit} style={{ padding: "0.5rem" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <Form.Group widths="equal">
        <Form.Field required error={!!formik.errors.area}>
          <label>Área</label>
          <Select
            search
            options={[
              ...areas,
              { key: "__add_area__", value: "__add_area__", text: "+ Nueva área" },
            ]}
            value={formik.values.area}
            onChange={(_, d) => {
              if (d.value === "__add_area__") {
                setShowAreaModal(true);
              } else {
                formik.setFieldValue("area", d.value);
              }
            }}
            placeholder="Seleccionar área"
          />
          {formik.errors.area && <span style={{ color: "red", fontSize: "0.8em" }}>{formik.errors.area}</span>}
        </Form.Field>
        <Form.Field required error={!!formik.errors.site}>
          <label>Sitio</label>
          <Select search options={sites} value={formik.values.site} onChange={sf("site")} placeholder="Seleccionar sitio" />
          {formik.errors.site && <span style={{ color: "red", fontSize: "0.8em" }}>{formik.errors.site}</span>}
        </Form.Field>
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Field>
          <label>Norma</label>
          <Select search options={sources} value={formik.values.source} onChange={sf("source")} placeholder="Seleccionar norma" clearable />
        </Form.Field>
        <Form.Field>
          <label>Requisito</label>
          <Select search options={requirements} value={formik.values.requirement} onChange={sf("requirement")} placeholder="Seleccionar requisito" clearable />
        </Form.Field>
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Field>
          <label>Autoridad</label>
          <Select search options={authorities} value={formik.values.authority} onChange={sf("authority")} placeholder="Seleccionar autoridad" clearable />
        </Form.Field>
        <Form.Field>
          <label>Responsable</label>
          <Select search options={responsibles} value={formik.values.responsible} onChange={sf("responsible")} placeholder="Seleccionar responsable" clearable />
        </Form.Field>
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Field>
          <label>Frecuencia</label>
          <Select search options={frequencies} value={formik.values.frequency} onChange={sf("frequency")} placeholder="Seleccionar frecuencia" clearable />
        </Form.Field>
      </Form.Group>

      <Form.Field>
        <label>Tipo de evidencia requerida</label>
        <Dropdown
          fluid multiple selection
          options={EVIDENCE_TYPE_OPTIONS}
          value={formik.values.evidenceTypes}
          onChange={(_, { value }) => formik.setFieldValue("evidenceTypes", value)}
          placeholder="Seleccionar tipos de evidencia"
        />
      </Form.Field>

      <Form.Group widths="equal">
        <Form.Field>
          <label>Vencimiento</label>
          <input type="date" value={formik.values.expiration} onChange={(e) => formik.setFieldValue("expiration", e.target.value)} />
        </Form.Field>
        <Form.Field>
          <label>Estado</label>
          <Select options={STATUS_OPTIONS} value={formik.values.status} onChange={sf("status")} placeholder="Seleccionar estado" clearable />
        </Form.Field>
      </Form.Group>

      <Form.Field>
        <label>Avance (%)</label>
        <input
          type="number" min={0} max={100}
          value={formik.values.progress}
          onChange={(e) => formik.setFieldValue("progress", Number(e.target.value))}
        />
      </Form.Field>

      <Form.Field>
        <label>Observaciones</label>
        <Form.TextArea
          rows={3}
          value={formik.values.observations}
          onChange={(e) => formik.setFieldValue("observations", e.target.value)}
          placeholder="Ingrese observaciones..."
        />
      </Form.Field>

      <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginTop: "1rem" }}>
        <Button primary type="submit" loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {item ? "Actualizar" : "Guardar"}
        </Button>
        <Button type="button" onClick={close}>Cancelar</Button>
      </div>

      <Modal open={showAreaModal} onClose={() => setShowAreaModal(false)} size="small">
        <Header icon="map marker alternate" content="Nueva Área" />
        <Modal.Content>
          <AreaForm
            area={null}
            close={() => setShowAreaModal(false)}
            onReload={() => reloadAreas(true)}
          />
        </Modal.Content>
      </Modal>
    </Form>
  );
}
